import { cloneElement, isValidElement, useEffect, useRef, type ReactElement, type ReactNode } from 'react'
import { useLocale } from '../i18n/LocaleContext'
import type { IntegrationTargetEntity } from '../../features/integrator/types'
import { useIntegrationProtection } from './useIntegrationProtection'

export function IntegrationManagedField({ label, managed, children }: {
  label: string
  managed: boolean
  children: ReactNode
}) {
  const { t } = useLocale()
  const control = managed && isValidElement(children)
    ? cloneElement(children as ReactElement<{ disabled?: boolean }>, { disabled: true })
    : children
  return <label className={managed ? 'integration-managed-field' : undefined}>
    <span>{label}</span>{control}
    {managed && <small>{t('integrator.managedField')}</small>}
  </label>
}

export function IntegrationManagedCheck({ label, managed, children }: {
  label: string
  managed: boolean
  children: ReactNode
}) {
  const { t } = useLocale()
  const control = managed && isValidElement(children)
    ? cloneElement(children as ReactElement<{ disabled?: boolean }>, { disabled: true })
    : children
  return <label className={`checkbox-row${managed ? ' integration-managed-field' : ''}`}>
    {control}<span>{label}</span>
    {managed && <small>{t('integrator.managedField')}</small>}
  </label>
}

/**
 * Adapts legacy forms that predate the shared field component. `fields` follows
 * the visual order of input/select/textarea controls; null marks UI-only values.
 * Repeated field names are supported for line editors.
 */
export function IntegrationProtectionBoundary({ entity, fields, children }: {
  entity: IntegrationTargetEntity
  fields: Array<string | null>
  children: ReactNode
}) {
  const root = useRef<HTMLDivElement>(null)
  const { t } = useLocale()
  const protection = useIntegrationProtection(entity)
  const signature = fields.join('|')
  useEffect(() => {
    const element = root.current
    if (!element) return
    const controls = [...element.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input:not([type="hidden"]):not([type="submit"]):not([type="button"]), select, textarea')]
    const changed: Array<{ control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement, disabled: boolean, title: string, label: HTMLElement | null, note: HTMLElement | null }> = []
    controls.forEach((control, index) => {
      const field = fields[index]
      if (!field || !protection.isProtected(field)) return
      const label = control.closest('label')
      const note = document.createElement('small')
      note.className = 'integration-managed-note'
      note.textContent = t('integrator.managedField')
      label?.classList.add('integration-managed-field')
      if (label && !label.querySelector('.integration-managed-note')) label.append(note)
      changed.push({ control, disabled: control.disabled, title: control.title, label, note: note.isConnected ? note : null })
      control.disabled = true
      control.title = t('integrator.managedField')
      control.closest('.entity-picker, .localized-input-with-picker')
        ?.querySelectorAll<HTMLButtonElement>('button').forEach(button => {
          changed.push({ control: button, disabled: button.disabled, title: button.title, label: null, note: null })
          button.disabled = true
          button.title = t('integrator.managedField')
        })
    })
    return () => changed.forEach(({ control, disabled, title, label, note }) => {
      control.disabled = disabled
      control.title = title
      note?.remove()
      label?.classList.remove('integration-managed-field')
    })
  }, [protection, signature, t])
  return <div ref={root} className="integration-protection-boundary">{children}</div>
}
